/**
 * Organization DTOs (`docs/design/00-contracts.md` §13.3; C-27, C-30).
 *
 * `GET /v1/organizations` runs with **no** org context: it deliberately spans every organization
 * the caller belongs to. Safety comes from the join to `org_memberships` on the caller's own
 * person id **plus** the RLS policy requiring the same membership — both must hold; neither is
 * decorative. Sorted by `name COLLATE "nb-NO-x-icu", id`, so Æ Ø Å sort after Z.
 */
import { z } from 'zod';

import { DEFAULT_COUNTRY_CODE, DEFAULT_TIMEZONE } from '../constants.js';
import { orgRoleSchema, organizationStatusSchema } from '../enums.js';
import { localeSchema } from '../locale.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { urlInputSchema } from '../url.js';
import { organizationAcceptedDocumentSchema } from './auth.js';

export const organizationSummaryDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    logoUrl: z.string().nullable(),
    status: organizationStatusSchema,
    createdAt: z.iso.datetime(),
    /** The caller's own role. Never any other person's role — that is Contacts, Phase 1. */
    myRole: orgRoleSchema,
  })
  .meta({ id: 'OrganizationSummary' });

export const organizationDtoSchema = organizationSummaryDtoSchema
  .extend({
    legalName: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    countryCode: z.string().length(2),
    timezone: z.string(),
    defaultLocale: localeSchema,
    billingEmail: z.email().nullable(),
    /** Fakturamottaker og fakturaadresse (1067, QA15 A2). Kontrakten henter dem til partene. */
    invoiceRecipient: z.string().nullable(),
    invoiceAddress: z.string().nullable(),
    /** #9: husets egne opplysninger, alle valgfrie. Se migrering 0291 for hvorfor. */
    address: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.email().nullable(),
    websiteUrl: z.string().nullable(),
    /** Logoen i toppen av eksportene (docs/EKSPORT.md §7). Uten logo står navnet alene. */
    exportShowLogo: z.boolean(),
    /**
     * BYRÅETS GENERELLE BOOKINGKONTAKT (0945, QA13 83): et medlem, vist på festivalens artistkort når
     * artisten ikke har en ansvarlig agent. Navnet er medlemmets, for velgeren.
     */
    bookingContactPersonId: z.uuid().nullable(),
    bookingContactName: z.string().nullable(),
  })
  .meta({ id: 'Organization' });

export const listOrganizationsQuerySchema = cursorQuerySchema(['name']).meta({
  id: 'ListOrganizationsQuery',
});

export const organizationListResponseSchema = page(
  organizationSummaryDtoSchema,
  'OrganizationListResponse',
);

export const createOrganizationRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(200),
    /** Derived from the name when omitted. */
    slug: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-z0-9][a-z0-9-]{1,62}$/)
      .optional(),
    countryCode: z.string().length(2).default(DEFAULT_COUNTRY_CODE),
    timezone: z.string().min(1).max(64).default(DEFAULT_TIMEZONE),
    /**
     * The superadmin accepts the customer terms AND the DPA on the organization's behalf. Both
     * must be present — it is not a checkbox the frontend may skip.
     */
    acceptedDocuments: z.array(organizationAcceptedDocumentSchema).length(2),
    signerName: z.string().trim().min(2).max(200),
    /** «Tittel». Den som signerer sin; blir også medlemskapets når den som signerer er deg. */
    signerJobTitle: z.string().trim().max(200).optional(),
    /**
     * DEN SOM OPPRETTER, OM SEG SELV (QA13 #10) — de samme feltene som invitasjonssiden har.
     *
     * Navnet sto før bare i innloggingens `user_metadata`, som API-et med vilje ikke stoler på, og
     * personen ble laget med e-posten som navn. Et felt som ikke ble vist (en konto som finnes fra
     * før), sendes ikke og røres ikke.
     */
    givenName: z.string().trim().min(1).max(200).optional(),
    familyName: z.string().trim().min(1).max(200).optional(),
    phone: z.string().trim().min(1).max(50).optional(),
    preferredLocale: localeSchema.optional(),
    /**
     * INVITASJONEN SOM GIR RETT TIL Å LAGE ET HUS (K-12, sak 170, 20. september).
     *
     * Ruta har `@OrgScope('none')` fordi den kjører FØR det finnes et hus å sjekke rettigheter
     * mot — så policylaget kan ikke svare på «har du lov». QA13 runde 2 fant følgen: enhver
     * autentisert konto kunne lage et hus og bli superadmin i det.
     *
     * Atle, sak 170: «En invitasjon av typen "nytt hus". Den som får den, lager huset og blir
     * superadmin. Endemålet er bare gründerportalen — dette er veien dit.»
     *
     * Tokenet er derfor påkrevd. Det løses inn i `app.claim_organization_invitation`, som merker
     * invitasjonen brukt i den samme setningen som den sjekker den — så to samtidige kall ikke
     * kan bruke den to ganger.
     */
    invitationToken: z.string().trim().min(1).max(200),
  })
  .meta({ id: 'CreateOrganizationRequest' });

/**
 * «Rediger egen organisasjon», øverst i Innstillinger › Organisasjon (#9).
 *
 * HVER FELT ER VALGFRITT, OG DET ER FORSKJELL PÅ UTELATT OG NULL: en nøkkel som ikke er med røres
 * ikke, og `null` tømmer feltet. Det er den samme regelen som `UpdateContactRequest` bruker, og
 * grunnen er den samme — et skjema som sender hele objektet skal kunne tømme ett felt uten at en
 * klient som sender ETT felt nullstiller resten.
 *
 * `slug`, `status` og `kind` står ikke her. Slug-en er en adresse andre har lagret, statusen er
 * abonnementets og `kind` avgjør menyen; ingen av de tre er noe et hus setter om seg selv i et
 * innstillingsskjema.
 */
export const updateOrganizationRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(200),
    legalName: z.string().trim().max(200).nullable(),
    registrationNumber: z.string().trim().max(50).nullable(),
    countryCode: z.string().length(2),
    timezone: z.string().min(1).max(64),
    defaultLocale: localeSchema,
    billingEmail: z.email().max(320).nullable(),
    invoiceRecipient: z.string().trim().max(200).nullable(),
    invoiceAddress: z.string().trim().max(500).nullable(),
    address: z.string().trim().max(500).nullable(),
    phone: z.string().trim().max(50).nullable(),
    email: z.email().max(320).nullable(),
    websiteUrl: urlInputSchema(2048).nullable(),
    logoUrl: z.string().trim().max(2048).nullable(),
    exportShowLogo: z.boolean(),
    /** Et medlem av huset, eller null. Serveren sjekker medlemskapet. */
    bookingContactPersonId: z.uuid().nullable(),
  })
  .partial()
  .meta({ id: 'UpdateOrganizationRequest' });

/**
 * FORESPØRSLENE SOM ER RETTET MOT DETTE HUSET (9a), slik huset ser dem.
 *
 * Samme form som en persons `MyPersonLink`, og av samme grunn: den som blir spurt skal se HVEM som
 * spør og ingenting om raden i registeret deres. Et hus kan stå i en annens register under et navn
 * de selv ikke har valgt, og det navnet er ikke vårt å vise fram her.
 */
export const organizationLinkRequestDtoSchema = z
  .object({
    id: z.uuid(),
    status: z.enum(['pending', 'accepted']),
    organizationName: z.string(),
    requestedByName: z.string(),
    createdAt: z.iso.datetime(),
    respondedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'OrganizationLinkRequest' });

export const organizationLinkRequestListResponseSchema = z
  .object({ items: z.array(organizationLinkRequestDtoSchema) })
  .meta({ id: 'OrganizationLinkRequestListResponse' });

export type OrganizationLinkRequestDto = z.infer<typeof organizationLinkRequestDtoSchema>;
export type OrganizationLinkRequestListResponse = z.infer<
  typeof organizationLinkRequestListResponseSchema
>;
export type OrganizationSummaryDto = z.infer<typeof organizationSummaryDtoSchema>;
export type UpdateOrganizationRequest = z.infer<typeof updateOrganizationRequestSchema>;
export type OrganizationDto = z.infer<typeof organizationDtoSchema>;
export type ListOrganizationsQuery = z.infer<typeof listOrganizationsQuerySchema>;
export type OrganizationListResponse = z.infer<typeof organizationListResponseSchema>;
export type CreateOrganizationRequest = z.infer<typeof createOrganizationRequestSchema>;

/**
 * «Ni siffer» gjelder bare norske hus (sak 215.5). Et svensk organisasjonsnummer har ti siffer og
 * bindestrek, et dansk CVR åtte — derfor sjekkes ingenting utenfor NO. Tomt er alltid lov.
 * Skjemaet og API-et leser den samme regelen herfra.
 */
export function registrationNumberIsValid(countryCode: string, value: string | null): boolean {
  if (value === null) return true;
  const compact = value.replace(/\s/g, '');
  if (compact === '') return true;
  return countryCode.toUpperCase() !== 'NO' || /^\d{9}$/.test(compact);
}
